<template>
  <div class="qa-qc-main well-page">

    <TabularLoaders v-if="showLoader" :columns="6" :rows="8" />
    
    <div v-if="allMappedSamples.length > 0" class="qaqc-main-container">
      <div class="qaqc-top-indicators qaqc-top-indicators-with-context">
        <div class="qaqc-top-indicators-left">
          <div
            v-if="showWellContext || hasReportContext"
            class="qaqc-context-block"
          >
            <div v-if="showWellContext" class="qaqc-context-pair">
              <span class="qaqc-context-title">Well:</span>
              <span class="qaqc-context-value">{{ displayWellName }}</span>
            </div>
            <div v-if="hasReportContext" class="qaqc-context-pair">
              <span class="qaqc-context-title">Report:</span>
              <span class="qaqc-context-value">{{ displayReportName }}</span>
            </div>
          </div>
        </div>
        <div class="qaqc-top-indicators-legend">
          <div class="qaqc-indicator-item">
            <div class="qaqc-indicator-dot passed"></div>
            <span>Passed</span>
          </div>
          <div class="qaqc-indicator-item">
            <div class="qaqc-indicator-dot failed"></div>
            <span>Failed</span>
          </div>
          <div class="qaqc-indicator-item">
            <div class="qaqc-indicator-dot pending"></div>
            <span>Pending</span>
          </div>
        </div>
      </div>
      <div class="qaqc-table-section">
        <div class="qaqc-detail-cards">
          <div
            v-if="showWellContext || hasReportContext"
            class="qaqc-detail-meta-card"
          >
            <div class="qaqc-detail-meta-title" v-if="showWellContext">
              Well: {{ displayWellName }}
            </div>
            <div class="qaqc-detail-meta-title" v-else-if="hasReportContext">
              {{ displayReportName }}
            </div>
            <div class="qaqc-detail-meta-grid">
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">{{ columnDisplayNames?.asset || 'Asset' }}</span>
                <span class="qaqc-detail-meta-value">{{ firstSample?.asset || '—' }}</span>
              </div>
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">{{ columnDisplayNames?.field || 'Field' }}</span>
                <span class="qaqc-detail-meta-value">{{ firstSample?.field || '—' }}</span>
              </div>
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">{{ columnDisplayNames?.reservoir || 'Reservoir' }}</span>
                <span class="qaqc-detail-meta-value">{{ firstSample?.reservoir || '—' }}</span>
              </div>
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">Report No.</span>
                <span class="qaqc-detail-meta-value">{{ firstSample?.reportIdentifier || displayReportName }}</span>
              </div>
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">{{ columnDisplayNames?.fluidType || 'Fluid Type' }}</span>
                <span class="qaqc-detail-meta-value">{{ firstSample?.fluidType || '—' }}</span>
              </div>
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">{{ columnDisplayNames?.fluidClassification || 'Fluid Classification' }}</span>
                <span class="qaqc-detail-meta-value">{{ firstSample?.fluidClassification || '—' }}</span>
              </div>
            </div>
          </div>

          <div
            v-else-if="isSampleSelectionContext && firstSample"
            class="qaqc-detail-meta-card"
          >
            <div class="qaqc-detail-meta-title">Sample ID: {{ firstSample.fluidSampleID }}</div>
            <div class="qaqc-detail-meta-grid">
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">{{ columnDisplayNames?.asset || 'Asset' }}</span>
                <span class="qaqc-detail-meta-value">{{ firstSample.asset || '—' }}</span>
              </div>
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">{{ columnDisplayNames?.field || 'Field' }}</span>
                <span class="qaqc-detail-meta-value">{{ firstSample.field || '—' }}</span>
              </div>
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">{{ columnDisplayNames?.reservoir || 'Reservoir' }}</span>
                <span class="qaqc-detail-meta-value">{{ firstSample.reservoir || '—' }}</span>
              </div>
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">Report No.</span>
                <span class="qaqc-detail-meta-value">{{ firstSample.reportIdentifier || '—' }}</span>
              </div>
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">{{ columnDisplayNames?.fluidType || 'Fluid Type' }}</span>
                <span class="qaqc-detail-meta-value">{{ firstSample.fluidType || '—' }}</span>
              </div>
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">{{ columnDisplayNames?.fluidClassification || 'Fluid Classification' }}</span>
                <span class="qaqc-detail-meta-value">{{ firstSample.fluidClassification || '—' }}</span>
              </div>
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">{{ columnDisplayNames?.reservoirPressure || 'Reservoir Pressure' }}</span>
                <span class="qaqc-detail-meta-value">{{ formatValueWithUOM(firstSample.reservoirPressure, 'reservoirPressure', firstSample.originalSample) }}</span>
              </div>
              <div class="qaqc-detail-meta-item">
                <span class="qaqc-detail-meta-label">{{ columnDisplayNames?.reservoirTemperature || 'Reservoir Temperature' }}</span>
                <span class="qaqc-detail-meta-value">{{ formatValueWithUOM(firstSample.reservoirTemperature, 'reservoirTemperature', firstSample.originalSample) }}</span>
              </div>
            </div>
          </div>

          <div v-if="isSampleSelectionContext" class="qaqc-detail-tests-heading">Tests</div>

          <div
            v-for="(item, index) in data"
            :key="index"
            class="qaqc-detail-sample-block"
          >
            <button
              v-if="!isSampleSelectionContext"
              type="button"
              class="qaqc-detail-sample-toggle"
              @click="toggleSampleExpanded(item.fluidSampleID || index)"
            >
              <span>Sample ID: {{ item.fluidSampleID }}</span>
              <span class="qaqc-detail-sample-chevron">
                <v-icon size="20">{{ isSampleExpanded(item.fluidSampleID || index) ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
              </span>
            </button>
            <div
              v-show="isSampleSelectionContext || isSampleExpanded(item.fluidSampleID || index)"
              class="qaqc-detail-test-grid"
            >
              <button
                v-for="(test, testIndex) in item.testsArray"
                :key="testIndex"
                type="button"
                class="qaqc-detail-test-card"
                @click="handleTestClick(test, item)"
              >
                <span class="qaqc-detail-test-name">{{ test.displayName }}</span>
                <span :class="[getTestStatusClass(test, item), 'qaqc-detail-test-badge']">
                  {{ getTestStatusLabel(test, item) }}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <div v-if="totalItems > 0" class="qaqc-detail-pagination-wrap">
        <Pagination
          variant="scorecard"
          :total-items="totalItems"
          v-model:current-page="currentPage"
          v-model:items-per-page="itemsPerPage"
          item-label="Samples"
          :page-sizes="[10, 20, 50, 100]"
          @update:current-page="onDetailPaginationPage"
          @update:items-per-page="onDetailPaginationItemsPerPage"
        />
      </div>
     </div>

    <div v-if="allMappedSamples.length === 0 && !showLoader && timeoutMessage === ''" class="no-asset-div">
      <div class="text-center">
        <v-icon color="grey" size="large" class="mb-2">mdi-database-off</v-icon>
        <h4>No data found</h4>
        <p>There is no data available for QA/QC review at this time.</p>
      </div>
    </div>

    <div v-else-if="timeoutMessage !== ''" class="no-asset-div timeout">
      <div class="text-center">
        <v-icon color="warning" size="large" class="mb-2">mdi-alert-circle</v-icon>
        <p>{{ timeoutMessage }}</p>
        <v-btn color="primary" @click="fetchData()" prepend-icon="mdi-reload">
          Retry
        </v-btn>
      </div>
    </div>
  </div>
</template>

<script>
import api from "../../axiosInterceptor";
import TabularLoaders from "../../components/layout/Loaders/TabularLoaders.vue";
import Pagination from "../../components/layout/Common/Pagination.vue";
import { getDisplayName } from "../../utils/cache.js";
import { dedupeSampleTests } from "@/utils/sampleTestsList.js";
import {
  parseSamplesListQueryFromRouteQuery,
  appendSamplesApiQueryParams,
  pickSamplesListQueryForRouter,
} from "../../components/layout/QAQC/Overview/qaqcSamplesListQuery.js";
import {
  mergeOverviewTabIntoQuery,
  overviewTabFromTestRoute,
} from "../../components/layout/QAQC/Overview/qaqcOverviewTab.js";

export default {
  name: "QAQCDetailView",
  components: {
    TabularLoaders,
    Pagination,
  },
  data() {
    return {
      activeTab: 'Tests',
      data: [],
      showLoader: true,
      timeoutMessage: "",
      currentPage: 1,
      itemsPerPage: Number(localStorage.getItem("itemsPerPage")) || 20,
      totalItems: 0,
      allMappedSamples: [],
      wellName: null,
      reportFilter: null,
      fluidSampleFilter: null,
      sampleIdFilter: null,
      apiListSelectedTests: null,
      apiListFluidType: null,
      columnDisplayNames: {
        fluidSampleID: 'Fluid Sample ID',
        asset: 'Asset',
        field: 'Field',
        reservoir: 'Reservoir',
        reportIdentifier: 'Report No.',
        fluidType: 'Fluid Type',
        fluidClassification: 'Fluid Classification',
        reservoirPressure: 'Reservoir Pressure',
        reservoirTemperature: 'Reservoir Temperature'
      },
      columnMetadata: {},
      expandedSamples: {},
    };
  },
  computed: {
    startIndex() {
      return (this.currentPage - 1) * this.itemsPerPage;
    },
    endIndex() {
      return Math.min(this.startIndex + this.itemsPerPage, this.totalItems);
    },
    displayWellName() {
      return this.wellName || "—";
    },
    hasReportContext() {
      return this.reportFilter != null && String(this.reportFilter).trim() !== "";
    },
    displayReportName() {
      if (!this.hasReportContext) {
        return "—";
      }
      return String(this.reportFilter);
    },
    showWellContext() {
      if (!this.wellName || String(this.wellName).trim() === "") return false;
      if (this.hasReportContext || this.isSampleSelectionContext) return false;
      return true;
    },
    isSampleSelectionContext() {
      if (this.$route?.name === "QAQCDetailSample") return true;
      const f = this.fluidSampleFilter != null && String(this.fluidSampleFilter).trim() !== "";
      const s = this.sampleIdFilter != null && String(this.sampleIdFilter).trim() !== "";
      return f || s;
    },
    firstSample() {
      return this.data?.[0] || null;
    },
  },

  methods: {
    goBack() {
      this.$router.go(-1);
    },
    
    async loadColumnDisplayNames() {
      try {
        const columnKeys = [
          'fluidSampleID',
          'asset',
          'field', 
          'reservoir',
          'reportIdentifier',
          'fluidType',
          'fluidClassification',
          'reservoirPressure',
          'reservoirTemperature'
        ];
        
        for (const key of columnKeys) {
          try {
            const result = await getDisplayName('sample', key);
            if (result && result.displayName) {
              this.columnDisplayNames[key] = result.displayName;
              this.columnMetadata[key] = {
                displayName: result.displayName,
                decimals: result.digits || 0
              };
            } else {
              this.columnDisplayNames[key] = this.formatHeaderName(key);
              this.columnMetadata[key] = {
                displayName: this.formatHeaderName(key),
                decimals: 0
              };
            }
          } catch (error) {
            this.columnDisplayNames[key] = this.formatHeaderName(key);
            this.columnMetadata[key] = {
              displayName: this.formatHeaderName(key),
              decimals: 0
            };
          }
        }
      } catch (error) {
        const columnKeys = [
          'fluidSampleID',
          'asset',
          'field', 
          'reservoir',
          'reportIdentifier',
          'fluidType',
          'fluidClassification',
          'reservoirPressure',
          'reservoirTemperature'
        ];
        
        columnKeys.forEach(key => {
          if (!this.columnDisplayNames[key]) {
            this.columnDisplayNames[key] = this.formatHeaderName(key);
            this.columnMetadata[key] = {
              displayName: this.formatHeaderName(key),
              decimals: 0
            };
          }
        });
      }
    },
    
    getUnitForProperty(key, dataSource) {
      const uomKey = key + '_UOM';
      if (dataSource && dataSource[uomKey]) {
        return dataSource[uomKey];
      }
      
      return "";
    },
    
    formatHeaderName(key) {
      return key
        .replace(/([A-Z])/g, ' $1')
        .replace(/_/g, ' ')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    },

    parseWellFromRoute(route) {
      const raw = route.params?.well;
      if (raw != null && String(raw).trim() !== "") {
        const s = String(raw);
        try {
          return decodeURIComponent(s);
        } catch {
          return s;
        }
      }
      const q = route.query?.well;
      if (q != null && String(q).trim() !== "") {
        return String(q);
      }
      return null;
    },

    decodeRouteParam(val) {
      if (val == null || String(val).trim() === "") return null;
      try {
        return decodeURIComponent(String(val));
      } catch {
        return String(val);
      }
    },

    parseSampleFiltersFromRoute(route) {
      const name = route.name;
      const p = route.params || {};
      if (name === "QAQCDetailSample") {
        return {
          reportIdentifier: null,
          fluidSampleID: this.decodeRouteParam(p.fluidSampleID),
          sampleId: this.decodeRouteParam(p.sampleId),
        };
      }
      if (name === "QAQCDetailReport") {
        return {
          reportIdentifier: this.decodeRouteParam(p.reportIdentifier),
          fluidSampleID: null,
          sampleId: this.decodeRouteParam(p.sampleId),
        };
      }
      const q = route.query || {};
      const pick = (key) => {
        const v = q[key];
        return v != null && String(v).trim() !== "" ? String(v) : null;
      };
      return {
        reportIdentifier: pick("reportIdentifier"),
        fluidSampleID: pick("fluidSampleID"),
        sampleId: pick("sampleId"),
      };
    },

    syncSampleFiltersFromRoute(route) {
      const f = this.parseSampleFiltersFromRoute(route);
      this.reportFilter = f.reportIdentifier;
      this.fluidSampleFilter = f.fluidSampleID;
      this.sampleIdFilter = f.sampleId;
      const listQ = parseSamplesListQueryFromRouteQuery(route.query || {});
      this.apiListSelectedTests = listQ.selectedTests;
      this.apiListFluidType = listQ.fluidType;
    },
    
    formatValueWithUOM(value, key, dataSource) {
      if (value === null || value === undefined || value === '-') {
        return value;
      }
      
      const metadata = this.columnMetadata?.[key];
      const decimals = metadata ? metadata.decimals : 0;
      const uom = this.getUnitForProperty(key, dataSource);
      
      let formattedValue = value;
      if (typeof value === 'number' || !isNaN(parseFloat(value))) {
        formattedValue = parseFloat(value).toFixed(decimals);
      }
      
      if (uom) {
        return `${formattedValue} ${uom}`;
      }
      
      return formattedValue;
    },
    onTabChange() {
      this.currentPage = 1;
      this.fetchData();
    },
    onDetailPaginationPage() {
      this.applyClientPagination();
    },
    onDetailPaginationItemsPerPage() {
      this.currentPage = 1;
      localStorage.setItem("itemsPerPage", String(this.itemsPerPage));
      this.applyClientPagination();
    },
    applyClientPagination() {
      const n = this.allMappedSamples.length;
      if (n === 0) {
        this.data = [];
        return;
      }
      const perPage = Math.max(1, this.itemsPerPage);
      const totalPages = Math.max(1, Math.ceil(n / perPage));
      if (this.currentPage > totalPages) {
        this.currentPage = totalPages;
      }
      const start = this.startIndex;
      const end = Math.min(start + perPage, n);
      this.data = this.allMappedSamples.slice(start, end);
    },
    async fetchData() {
      this.showLoader = true;
      this.timeoutMessage = "";
      
      try {
        await this.loadColumnDisplayNames();
        
        if (this.wellName) {
          await this.fetchWellSamples();
        } else {
          this.data = [];
          this.allMappedSamples = [];
          this.totalItems = 0;
        }
      } catch (error) {
        this.timeoutMessage = "Failed to load data. Please try again.";
        this.data = [];
        this.allMappedSamples = [];
        this.totalItems = 0;
      } finally {
      this.showLoader = false;
      }
    },
    async fetchWellSamples() {
      try {
        let url = `/public/api/samples?well="${this.wellName}"&viewSummary=true`;
        if (this.reportFilter) {
          url += `&reportIdentifier="${this.reportFilter}"`;
        }
        if (this.fluidSampleFilter) {
          url += `&fluidSampleID="${this.fluidSampleFilter}"`;
        }
        if (this.sampleIdFilter) {
          url += `&sampleId="${this.sampleIdFilter}"`;
        }
        url = appendSamplesApiQueryParams(url, {
          // selectedTests: this.apiListSelectedTests,
          fluidType: this.apiListFluidType,
        });
        const response = await api.get(url);

        if (response.data && response.data.data && response.data.data.samples && response.data.data.samples.length > 0) {
          this.mapApiDataToTable(response.data.data.samples);
          this.totalItems = this.allMappedSamples.length;
        } else {
          this.data = [];
          this.allMappedSamples = [];
          this.totalItems = 0;
        }
      } catch (error) {
        throw error;
      }
    },
    mapApiDataToTable(apiData) {
      const mappedData = [];
      
      apiData.forEach((sample, index) => {
        const testsArray = this.extractTestsFromSample(sample);
        
        const mappedItem = {
          id: sample.ID || index + 1,
          fluidSampleID: sample.fluidSampleID || sample.uniqueID || `SMP-${index + 1}`,
          testsArray: testsArray,
          asset: sample.location?.asset || '-',
          field: sample.location?.field || '-',
          reservoir: sample.location?.reservoir || '-',
          reportIdentifier: sample.reportIdentifier || sample.ReportIdentifier || '-',
          fluidType: sample.fluidType || '-',
          fluidClassification: this.getFluidClassification(sample),
          reservoirPressure: this.getReservoirPressure(sample),
          testTemperature: this.getTestTemperature(sample),
          reservoirTemperature: this.getReservoirTemperature(sample),
          tTestSelected: false,
          tResSelected: false,
          qcStatus: this.getQCStatus(sample),
          originalSample: sample
        };
        
        mappedData.push(mappedItem);
      });

      this.allMappedSamples = mappedData;
      this.applyClientPagination();
    },
    extractTestsFromSample(sample) {
      const tests = [];
      
      if (sample.compositiontests && sample.compositiontests.length > 0) {
        sample.compositiontests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'Composition',
            testType: 'compositiontests',
            TestType: 'compositiontests', 
            test_type: 'compositiontests', 
            testId: testId,
            ID: testId, 
            id: testId, 
            testData: test,
            displayName: this.getCompositionDisplayName(test, sample.compositiontests.length, index),
            index: index,
            contaminationType: test.contaminationType,
            recombinationType: test.recombinationType,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      
      if (sample.ccetests && sample.ccetests.length > 0) {
        sample.ccetests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'CCE Test',
            testType: 'ccetests',
            TestType: 'ccetests',
            test_type: 'ccetests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'CCE', sample.ccetests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      
      if (sample.viscositytests && sample.viscositytests.length > 0) {
        sample.viscositytests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'Viscosity Test',
            testType: 'viscositytests',
            TestType: 'viscositytests',
            test_type: 'viscositytests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'Viscosity Test', sample.viscositytests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      if (sample.dltests && sample.dltests.length > 0) {
        sample.dltests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'DL Test',
            testType: 'dltests',
            TestType: 'dltests',
            test_type: 'dltests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'DL', sample.dltests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      if (sample.cvdtests && sample.cvdtests.length > 0) {
        sample.cvdtests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'CVD Test',
            testType: 'cvdtests',
            TestType: 'cvdtests',
            test_type: 'cvdtests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'CVD', sample.cvdtests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      
      if (sample.separatortests && sample.separatortests.length > 0) {
        sample.separatortests.forEach((test, index) => {
          const testId = test.ID || test._id || test.id;
          tests.push({
            type: 'Separator Test',
            testType: 'separatortests',
            TestType: 'separatortests',
            test_type: 'separatortests',
            testId: testId,
            ID: testId,
            id: testId,
            testData: test,
            displayName: this.getTestDisplayName(test, 'Separator Test', sample.separatortests.length, index),
            index: index,
            testTemperature: test.testTemperature,
            testTemperature_UOM: test.testTemperature_UOM
          });
        });
      }
      if (sample.IsTestAvailable) {
        const testId = sample.ID || sample._id || sample.id;
        tests.push({
          type: 'Sample Validation',
          testType: 'sampleValidation',
          TestType: 'sampleValidation',
          test_type: 'sampleValidation',
          testId: testId,
          ID: testId,
          id: testId,
          testData: sample,
          displayName: 'Sample Validation',
          index: 0
        });
      }
      if (tests.length === 0) {
        const testId = sample.ID || sample._id || sample.id;
        tests.push({
          type: 'Sample Validation',
          testType: 'sampleValidation',
          TestType: 'sampleValidation',
          test_type: 'sampleValidation',
          testId: testId,
          ID: testId,
          id: testId,
          testData: sample,
          displayName: 'Sample Validation',
          index: 0
        });
      }
      
      return dedupeSampleTests(tests);
    },
    
    getCompositionDisplayName(test, totalCount, index) {
      let displayName = 'Composition';
      
      if (test.contaminationType || test.recombinationType) {
        displayName += ' (';
        if (test.contaminationType) {
          displayName += test.contaminationType;
        } else if (test.recombinationType) {
          displayName += test.recombinationType;
        }
        displayName += ')';
      }
      
      if (totalCount > 1) {
        displayName += ` (Test #${index + 1})`;
      }
      
      return displayName;
    },
    
    getTestDisplayName(test, testType, totalCount, index) {
      let displayName = test.name || testType;
      
      if (test.testTemperature) {
        const uom = test.testTemperature_UOM || '';
        displayName += ` (${test.testTemperature}${uom ? ` ${uom}` : ''})`;
      } else if (totalCount > 1) {
        displayName += ` (Test #${index + 1})`;
      }
      
      return displayName;
    },
    
    getFluidClassification(sample) {
      if (sample.fluidType === 'Oil') {
        return 'Oil';
      } else if (sample.fluidType === 'Gas') {
        return 'Gas';
      } else if (sample.fluidType) {
        return sample.fluidType;
      }
      return '-';
    },
    getReservoirPressure(sample) {
      if (sample.sampleProperties?.reservoirPressure) {
        return sample.sampleProperties.reservoirPressure;
      }
      return '-';
    },
    getTestTemperature(sample) {
      if (sample.testTemperature != null) {
        return sample.testTemperature;
      }
      if (sample.sampleProperties?.testTemperature != null) {
        return sample.sampleProperties.testTemperature;
      }
      if (sample.standardTemperature != null) {
        return sample.standardTemperature;
      }
      return '-';
    },
    getReservoirTemperature(sample) {
      if (sample.reservoirTemperature) {
        return sample.reservoirTemperature;
      }
      if (sample.sampleProperties?.reservoirTemperature) {
        return sample.sampleProperties.reservoirTemperature;
      }
      if (sample.standardTemperature) {
        return sample.standardTemperature;
      }
      return '-';
    },
    getQCStatus(sample) {
      if (sample.compositiontests && sample.compositiontests.length > 0) {
        return 'Passed';
      }
      return 'Pending';
    },
     
     handleTestClick(test, item) {
       const sampleId = item.originalSample?._id || item.originalSample?.ID || item.id;
       const testId = test.testId;
       
       const testTypeMap = {
         'CCE Test': 'ccetest',
         'DL Test': 'dltest', 
         'CVD Test': 'cvdtest',
         'Viscosity Test': 'viscositytest',
         'Separator Test': 'separatortest',
         'Composition': 'compositiontest',
         'Sample Validation': 'samplevalidation'
       };
       
       const testType = testTypeMap[test.type] || 'ccetest';
       
       const nav = {
         name: "UnifiedTestView",
         params: {
           sampleId: String(sampleId),
           testType: testType,
           testId: String(testId),
         },
       };
       const q = { ...pickSamplesListQueryForRouter(this.$route.query || {}) };
       if (this.wellName) {
         q.well = this.wellName;
       }
       if (this.reportFilter) {
         q.reportIdentifier = this.reportFilter;
       }
       if (item.fluidSampleID != null && String(item.fluidSampleID).trim() !== "") {
         q.fluidSampleID = String(item.fluidSampleID);
       }
       q.sampleId = String(sampleId);
       if (this.$route.name === "QAQCDetailSample" || (this.isSampleSelectionContext && !this.hasReportContext)) {
         q.qaqcCtx = "sample";
       } else if (this.hasReportContext || this.$route.name === "QAQCDetailReport") {
         q.qaqcCtx = "report";
       } else if (this.wellName) {
         q.qaqcCtx = "well";
       } else {
         q.qaqcCtx = "direct";
       }
       nav.query = mergeOverviewTabIntoQuery(
         q,
         overviewTabFromTestRoute({
           params: { testType },
           query: q,
         })
       );
       this.$router.push(nav);
     },
     
    getQCStatusColor(status) {
      const statusColors = {
        'Passed': 'success',
        'Pending': 'warning'
      };
      return statusColors[status] || 'grey';
    },
    getTestStatusClass(test, item) {
      const sample = item.originalSample;
      if (!sample) {
        return 'test-status-pending clickable-test';
      }
      
      if (test.type === 'Sample Validation') {
        if (sample.IsTestAvailable) {
          return 'test-status-passed clickable-test';
        } else {
          return 'test-status-pending clickable-test';
        }
      }
      
      if (test.testData && test.testData.hasOwnProperty('IsVerified')) {
        const isVerifiedValue = test.testData.IsVerified;
        
        if (isVerifiedValue === true || isVerifiedValue === 1 || isVerifiedValue === '1') {
          return 'test-status-passed clickable-test';
        } else if (isVerifiedValue === false || isVerifiedValue === 0 || isVerifiedValue === '0') {
          return 'test-status-failed clickable-test';
        } else {
          return 'test-status-pending clickable-test';
        }
      }
      
      return 'test-status-pending clickable-test';
    },
    getTestStatusLabel(test, item) {
      const cls = this.getTestStatusClass(test, item) || "";
      if (cls.includes("test-status-passed")) return "Passed";
      if (cls.includes("test-status-failed")) return "Failed";
      return "Pending";
    },
    isSampleExpanded(id) {
      return this.expandedSamples[id] !== false;
    },
    toggleSampleExpanded(id) {
      this.expandedSamples = {
        ...this.expandedSamples,
        [id]: this.expandedSamples[id] === false,
      };
    }
  },
  watch: {
    '$route'(to, from) {
      const nextWell = this.parseWellFromRoute(to);
      const prevWell = from ? this.parseWellFromRoute(from) : null;
      const nextF = this.parseSampleFiltersFromRoute(to);
      const prevF = from ? this.parseSampleFiltersFromRoute(from) : null;
      const nextList = parseSamplesListQueryFromRouteQuery(to.query || {});
      const prevList = from ? parseSamplesListQueryFromRouteQuery(from.query || {}) : { selectedTests: null, fluidType: null };

      this.wellName = nextWell;
      this.syncSampleFiltersFromRoute(to);

      const listFiltersChanged =
        JSON.stringify(nextList.selectedTests || []) !== JSON.stringify(prevList.selectedTests || []) ||
        String(nextList.fluidType || "") !== String(prevList.fluidType || "");
      const filtersChanged =
        nextF.reportIdentifier !== prevF.reportIdentifier ||
        nextF.fluidSampleID !== prevF.fluidSampleID ||
        nextF.sampleId !== prevF.sampleId ||
        listFiltersChanged;
      const routeVariantChanged = from && to.name !== from.name;

      if (nextWell !== prevWell || filtersChanged || routeVariantChanged) {
        this.currentPage = 1;
        if (this.wellName) {
          this.fetchData();
        } else {
          this.data = [];
          this.allMappedSamples = [];
          this.totalItems = 0;
        }
      }
    }
  },
  mounted() {
    this.wellName = this.parseWellFromRoute(this.$route);
    this.syncSampleFiltersFromRoute(this.$route);

    if (this.wellName) {
      this.fetchData();
    } else {
      this.data = [];
      this.allMappedSamples = [];
      this.totalItems = 0;
    }
  },
};
</script>
